import { ref, watchEffect, onBeforeUnmount, getCurrentInstance } from "vue";

/**
 * Reactive media query composable
 * @param query - CSS media query string (e.g., "(min-width: 768px)")
 * @returns reactive ref that tracks if the media query matches
 */
export function useMediaQuery(query: string) {
  const matches = ref(false);
  let mediaQueryList: MediaQueryList | null = null;
  let cleanup: (() => void) | null = null;

  const updateMatches = (e: MediaQueryListEvent | MediaQueryList) => {
    matches.value = e.matches;
  };

  watchEffect((onInvalidate) => {
    if (typeof window !== "undefined" && window.matchMedia) {
      mediaQueryList = window.matchMedia(query);
      matches.value = mediaQueryList.matches;

      // Add event listener for changes
      if (mediaQueryList.addEventListener) {
        mediaQueryList.addEventListener("change", updateMatches);
      } else {
        // Fallback for older browsers
        mediaQueryList.addListener(updateMatches);
      }
      cleanup = () => {
        if (!mediaQueryList) return;
        if (mediaQueryList.removeEventListener) {
          mediaQueryList.removeEventListener("change", updateMatches);
        } else {
          // Fallback for older browsers
          mediaQueryList.removeListener(updateMatches);
        }
      };
    }

    // Ensure listeners are removed when the effect is invalidated (e.g., during tests or reactivity changes)
    onInvalidate(() => {
      cleanup?.();
      cleanup = null;
    });
  });

  // Only register lifecycle cleanup if we're inside a component instance
  if (getCurrentInstance()) {
    onBeforeUnmount(() => {
      cleanup?.();
      cleanup = null;
    });
  }

  return { matches };
}

/**
 * Common breakpoint utilities
 */
export const useBreakpoints = () => {
  const isMobile = useMediaQuery("(max-width: 767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1023px)");
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const isLargeDesktop = useMediaQuery("(min-width: 1024px)");

  return {
    isMobile: isMobile.matches,
    isTablet: isTablet.matches,
    isDesktop: isDesktop.matches,
    isLargeDesktop: isLargeDesktop.matches,
  };
};
